import { createAction } from 'redux-actions';
import { getLibrarySelectedImage, previewImage } from '@nextiva/engagement-ui';
import { openModalDialog } from '@nextiva/crm-modals';

import ActionsTypes from '../const/ActionsTypes';
import modalTypes from '../../../constants/AppModalTypes';
import {
  getActiveEntityCollectionPath,
  getEditorEntityId,
  getEditorOrderedBlockList,
} from '../../../reducers';
import api from '../../../api';
import stylingSelectors from '../selectors';
import { updateBlockListBulk } from '../../../actions/surveyEditorActions';
import { unsetBlockInlineStyles } from '../utils/unsetBlockInlineStyles';
import { handleApiErrors } from '../../../actions/actionsUtils';

const { getLocalStyling } = stylingSelectors;

const {
  SET_LOCAL_STYLING,
  SET_LOCAL_LABEL_FONT_COLOR,
  SET_LOCAL_LABEL_FONT_SIZE,
  SET_LOCAL_LABEL_FONT_STYLE,
  SET_LOCAL_LABEL_FONT_WEIGHT,
  SET_LOCAL_LABEL_STICK_TO,
  SET_LOCAL_LABEL_TEXT_DECORATION,
  SET_LOCAL_LABEL_TEXT_TRANSFORM,
  SET_LOCAL_SELECTION_COLOR,
  SET_LOCAL_TEXTFIELD_BACKGROUND_COLOR,
  SET_LOCAL_TEXTFIELD_BORDER_COLOR,
  SET_LOCAL_TEXTFIELD_BORDER_RADIUS,
  SET_LOCAL_TEXTFIELD_BORDER_STYLE,
  SET_LOCAL_TEXTFIELD_BORDER_WIDTH,
  SET_LOCAL_TEXTFIELD_FONT_COLOR,
  SET_LOCAL_TEXTFIELD_FONT_SIZE,
  SET_LOCAL_TEXTFIELD_FONT_STYLE,
  SET_LOCAL_TEXTFIELD_FONT_WEIGHT,
  SET_LOCAL_TEXTFIELD_TEXT_DECORATION,
  SET_LOCAL_TEXTFIELD_TEXT_TRANSFORM,
  FINISH_LOCAL_STYLING_PERSISTENCE,
  SET_STATIC_TEXT_FONT_SIZE,
  SET_STATIC_TEXT_FONT_FAMILY,
  SET_STATIC_TEXT_FONT_COLOR,
  SET_STATIC_TEXT_FONT_STYLE,
  SET_STATIC_TEXT_FONT_WEIGHT,
  SET_STATIC_TEXT_DECORATION,
  SET_STATIC_TEXT_TRANSFORM,
  SET_QUESTION_TEXT_FONT_SIZE,
  SET_QUESTION_TEXT_FONT_FAMILY,
  SET_QUESTION_TEXT_FONT_COLOR,
  SET_QUESTION_TEXT_FONT_STYLE,
  SET_QUESTION_TEXT_FONT_WEIGHT,
  SET_QUESTION_TEXT_DECORATION,
  SET_QUESTION_TEXT_TRANSFORM,
  SET_OPTION_TEXT_FONT_SIZE,
  SET_OPTION_TEXT_FONT_FAMILY,
  SET_OPTION_TEXT_FONT_COLOR,
  SET_OPTION_TEXT_FONT_STYLE,
  SET_OPTION_TEXT_FONT_WEIGHT,
  SET_OPTION_TEXT_DECORATION,
  SET_OPTION_TEXT_TRANSFORM,
  SET_BUTTON_FONT_SIZE,
  SET_BUTTON_FONT_COLOR,
  SET_BUTTON_FONT_WEIGHT,
  SET_BUTTON_FONT_STYLE,
  SET_BUTTON_TEXT_DECORATION,
  SET_BUTTON_TEXT_TRANSFORM,
  SET_BUTTON_BORDER_WIDTH,
  SET_BUTTON_BORDER_STYLE,
  SET_BUTTON_BORDER_RADIUS,
  SET_BUTTON_BORDER_COLOR,
  SET_BUTTON_BACKGROUND_COLOR,
  SET_PAGE_BACKGROUND_COLOR,
  SET_BODY_BACKGROUND_COLOR,
  SET_BODY_BACKGROUND_IMAGE,
  SET_BODY_BACKGROUND_POSITION,
  SET_BLOCK_MARGIN,
} = ActionsTypes;

export const setLocalStyling = createAction(SET_LOCAL_STYLING);
export const finishLocalStylingPersistence = createAction(
  FINISH_LOCAL_STYLING_PERSISTENCE,
);
const setSelectionColor = createAction(SET_LOCAL_SELECTION_COLOR);
const setPageBackgroundColor = createAction(SET_PAGE_BACKGROUND_COLOR);
const setBodyBackgroundColor = createAction(SET_BODY_BACKGROUND_COLOR);
const setBodyBackgroundImage = createAction(
  SET_BODY_BACKGROUND_IMAGE,
  // define image shape
  image => {
    if (!image) {
      return image;
    }
    const { height, id, name, url, width } = image;
    return { height, id, name, url, width };
  },
);
const setBodyBackgroundPosition = createAction(SET_BODY_BACKGROUND_POSITION);
const setBlockMargin = createAction(SET_BLOCK_MARGIN);

const setStaticTextFontSize = createAction(SET_STATIC_TEXT_FONT_SIZE);
const setStaticTextFontFamily = createAction(SET_STATIC_TEXT_FONT_FAMILY);
const setStaticTextFontColor = createAction(SET_STATIC_TEXT_FONT_COLOR);
const setStaticTextFontStyle = createAction(SET_STATIC_TEXT_FONT_STYLE);
const setStaticTextFontWeight = createAction(SET_STATIC_TEXT_FONT_WEIGHT);
const setStaticTextDecoration = createAction(SET_STATIC_TEXT_DECORATION);
const setStaticTextTransform = createAction(SET_STATIC_TEXT_TRANSFORM);
const setQuestionTextFontSize = createAction(SET_QUESTION_TEXT_FONT_SIZE);
const setQuestionTextFontFamily = createAction(SET_QUESTION_TEXT_FONT_FAMILY);
const setQuestionTextFontColor = createAction(SET_QUESTION_TEXT_FONT_COLOR);
const setQuestionTextFontStyle = createAction(SET_QUESTION_TEXT_FONT_STYLE);
const setQuestionTextFontWeight = createAction(SET_QUESTION_TEXT_FONT_WEIGHT);
const setQuestionTextDecoration = createAction(SET_QUESTION_TEXT_DECORATION);
const setQuestionTextTransform = createAction(SET_QUESTION_TEXT_TRANSFORM);
const setOptionTextFontSize = createAction(SET_OPTION_TEXT_FONT_SIZE);
const setOptionTextFontFamily = createAction(SET_OPTION_TEXT_FONT_FAMILY);
const setOptionTextFontColor = createAction(SET_OPTION_TEXT_FONT_COLOR);
const setOptionTextFontStyle = createAction(SET_OPTION_TEXT_FONT_STYLE);
const setOptionTextFontWeight = createAction(SET_OPTION_TEXT_FONT_WEIGHT);
const setOptionTextDecoration = createAction(SET_OPTION_TEXT_DECORATION);
const setOptionTextTransform = createAction(SET_OPTION_TEXT_TRANSFORM);
const setButtonFontSize = createAction(SET_BUTTON_FONT_SIZE);
const setButtonFontColor = createAction(SET_BUTTON_FONT_COLOR);
const setButtonFontWeight = createAction(SET_BUTTON_FONT_WEIGHT);
const setButtonFontStyle = createAction(SET_BUTTON_FONT_STYLE);
const setButtonTextDecoration = createAction(SET_BUTTON_TEXT_DECORATION);
const setButtonTextTransform = createAction(SET_BUTTON_TEXT_TRANSFORM);
const setButtonBorderWidth = createAction(SET_BUTTON_BORDER_WIDTH);
const setButtonBorderStyle = createAction(SET_BUTTON_BORDER_STYLE);
const setButtonBorderRadius = createAction(SET_BUTTON_BORDER_RADIUS);
const setButtonBorderColor = createAction(SET_BUTTON_BORDER_COLOR);
const setLabelFontColor = createAction(SET_LOCAL_LABEL_FONT_COLOR);
const setLabelFontSize = createAction(SET_LOCAL_LABEL_FONT_SIZE);
const setLabelFontStyle = createAction(SET_LOCAL_LABEL_FONT_STYLE);
const setLabelFontWeight = createAction(SET_LOCAL_LABEL_FONT_WEIGHT);
const setLabelStickTo = createAction(SET_LOCAL_LABEL_STICK_TO);
const setLabelTextDecoration = createAction(SET_LOCAL_LABEL_TEXT_DECORATION);
const setLabelTextTransform = createAction(SET_LOCAL_LABEL_TEXT_TRANSFORM);
const setTextfieldBackgroundColor = createAction(
  SET_LOCAL_TEXTFIELD_BACKGROUND_COLOR,
);
const setTextfieldBorderColor = createAction(SET_LOCAL_TEXTFIELD_BORDER_COLOR);
const setTextfieldBorderRadius = createAction(
  SET_LOCAL_TEXTFIELD_BORDER_RADIUS,
);
const setTextfieldBorderStyle = createAction(SET_LOCAL_TEXTFIELD_BORDER_STYLE);
const setTextfieldBorderWidth = createAction(SET_LOCAL_TEXTFIELD_BORDER_WIDTH);
const setTextfieldFontColor = createAction(SET_LOCAL_TEXTFIELD_FONT_COLOR);
const setTextfieldFontSize = createAction(SET_LOCAL_TEXTFIELD_FONT_SIZE);
const setTextfieldFontStyle = createAction(SET_LOCAL_TEXTFIELD_FONT_STYLE);
const setTextfieldFontWeight = createAction(SET_LOCAL_TEXTFIELD_FONT_WEIGHT);
const setTextfieldTextDecoration = createAction(
  SET_LOCAL_TEXTFIELD_TEXT_DECORATION,
);
const setTextfieldTextTransform = createAction(
  SET_LOCAL_TEXTFIELD_TEXT_TRANSFORM,
);

const setButtonBackgroundColor = createAction(SET_BUTTON_BACKGROUND_COLOR);

/* removes inline block styles that overlap styles set globally */
export const removeAndPersistInlineBlockStyles = (dispatch, getState) => {
  const state = getState();
  const currentBlockList = getEditorOrderedBlockList(state);
  const noStylingBlockList = currentBlockList.map(unsetBlockInlineStyles);
  dispatch(updateBlockListBulk(noStylingBlockList));

  return api
    .updateBlockBulk(
      getActiveEntityCollectionPath(state),
      getEditorEntityId(state),
      noStylingBlockList,
    )
    .catch(rejection => {
      dispatch(updateBlockListBulk(currentBlockList));
      dispatch(handleApiErrors(rejection));
    });
};

const persistCurrentLocalStyling = (state, dispatch) =>
  api
    .changeLocalStyling(
      getActiveEntityCollectionPath(state),
      getEditorEntityId(state),
      getLocalStyling(state),
    )
    .then(() => dispatch(finishLocalStylingPersistence()))
    .catch(rejection => dispatch(handleApiErrors(rejection)));

const changeAndPersistLocalStyleActionFactory = action => value => async (
  dispatch,
  getState,
) => {
  const rollBackStylingState = getLocalStyling(getState());
  dispatch(action(value));
  try {
    await persistCurrentLocalStyling(getState(), dispatch);
  } catch (e) {
    dispatch(setLocalStyling(rollBackStylingState));
  }
};

export const changeSelectionColor = changeAndPersistLocalStyleActionFactory(
  setSelectionColor,
);

export const changeStaticTextFontSize = changeAndPersistLocalStyleActionFactory(
  setStaticTextFontSize,
);
export const changeStaticTextFontFamily = changeAndPersistLocalStyleActionFactory(
  setStaticTextFontFamily,
);
export const changeStaticTextFontColor = changeAndPersistLocalStyleActionFactory(
  setStaticTextFontColor,
);
export const changeStaticTextFontStyle = changeAndPersistLocalStyleActionFactory(
  setStaticTextFontStyle,
);
export const changeStaticTextFontWeight = changeAndPersistLocalStyleActionFactory(
  setStaticTextFontWeight,
);
export const changeStaticTextDecoration = changeAndPersistLocalStyleActionFactory(
  setStaticTextDecoration,
);
export const changeStaticTextTransform = changeAndPersistLocalStyleActionFactory(
  setStaticTextTransform,
);

export const changeQuestionTextFontSize = changeAndPersistLocalStyleActionFactory(
  setQuestionTextFontSize,
);

export const changeQuestionTextFontFamily = changeAndPersistLocalStyleActionFactory(
  setQuestionTextFontFamily,
);

export const changeQuestionTextFontColor = changeAndPersistLocalStyleActionFactory(
  setQuestionTextFontColor,
);
export const changeQuestionTextFontStyle = changeAndPersistLocalStyleActionFactory(
  setQuestionTextFontStyle,
);
export const changeQuestionTextFontWeight = changeAndPersistLocalStyleActionFactory(
  setQuestionTextFontWeight,
);
export const changeQuestionTextDecoration = changeAndPersistLocalStyleActionFactory(
  setQuestionTextDecoration,
);
export const changeQuestionTextTransform = changeAndPersistLocalStyleActionFactory(
  setQuestionTextTransform,
);

export const changeOptionTextFontSize = changeAndPersistLocalStyleActionFactory(
  setOptionTextFontSize,
);

export const changeOptionTextFontFamily = changeAndPersistLocalStyleActionFactory(
  setOptionTextFontFamily,
);

export const changeOptionTextFontColor = changeAndPersistLocalStyleActionFactory(
  setOptionTextFontColor,
);
export const changeOptionTextFontStyle = changeAndPersistLocalStyleActionFactory(
  setOptionTextFontStyle,
);
export const changeOptionTextFontWeight = changeAndPersistLocalStyleActionFactory(
  setOptionTextFontWeight,
);
export const changeOptionTextDecoration = changeAndPersistLocalStyleActionFactory(
  setOptionTextDecoration,
);
export const changeOptionTextTransform = changeAndPersistLocalStyleActionFactory(
  setOptionTextTransform,
);

export const changeButtonFontSize = changeAndPersistLocalStyleActionFactory(
  setButtonFontSize,
);

export const changeButtonFontColor = changeAndPersistLocalStyleActionFactory(
  setButtonFontColor,
);

export const changeButtonFontWeight = changeAndPersistLocalStyleActionFactory(
  setButtonFontWeight,
);

export const changeButtonFontStyle = changeAndPersistLocalStyleActionFactory(
  setButtonFontStyle,
);

export const changeButtonTextDecoration = changeAndPersistLocalStyleActionFactory(
  setButtonTextDecoration,
);

export const changeButtonTextTransform = changeAndPersistLocalStyleActionFactory(
  setButtonTextTransform,
);

export const changeButtonBorderWidth = changeAndPersistLocalStyleActionFactory(
  setButtonBorderWidth,
);

export const changeButtonBorderStyle = changeAndPersistLocalStyleActionFactory(
  setButtonBorderStyle,
);

export const changeButtonBorderRadius = changeAndPersistLocalStyleActionFactory(
  setButtonBorderRadius,
);

export const changeButtonBorderColor = changeAndPersistLocalStyleActionFactory(
  setButtonBorderColor,
);

export const changeButtonBackgroundColor = changeAndPersistLocalStyleActionFactory(
  setButtonBackgroundColor,
);

export const changeLabelFontColor = changeAndPersistLocalStyleActionFactory(
  setLabelFontColor,
);
export const changeLabelFontSize = changeAndPersistLocalStyleActionFactory(
  setLabelFontSize,
);
export const changeLabelFontStyle = changeAndPersistLocalStyleActionFactory(
  setLabelFontStyle,
);
export const changeLabelFontWeight = changeAndPersistLocalStyleActionFactory(
  setLabelFontWeight,
);
export const changeLabelStickTo = changeAndPersistLocalStyleActionFactory(
  setLabelStickTo,
);
export const changeLabelTextDecoration = changeAndPersistLocalStyleActionFactory(
  setLabelTextDecoration,
);
export const changeLabelTextTransform = changeAndPersistLocalStyleActionFactory(
  setLabelTextTransform,
);
export const changeTextfieldBackgroundColor = changeAndPersistLocalStyleActionFactory(
  setTextfieldBackgroundColor,
);
export const changeTextfieldBorderColor = changeAndPersistLocalStyleActionFactory(
  setTextfieldBorderColor,
);
export const changeTextfieldBorderRadius = changeAndPersistLocalStyleActionFactory(
  setTextfieldBorderRadius,
);
export const changeTextfieldBorderStyle = changeAndPersistLocalStyleActionFactory(
  setTextfieldBorderStyle,
);
export const changeTextfieldBorderWidth = changeAndPersistLocalStyleActionFactory(
  setTextfieldBorderWidth,
);
export const changeTextfieldFontColor = changeAndPersistLocalStyleActionFactory(
  setTextfieldFontColor,
);
export const changeTextfieldFontSize = changeAndPersistLocalStyleActionFactory(
  setTextfieldFontSize,
);
export const changeTextfieldFontStyle = changeAndPersistLocalStyleActionFactory(
  setTextfieldFontStyle,
);
export const changeTextfieldFontWeight = changeAndPersistLocalStyleActionFactory(
  setTextfieldFontWeight,
);
export const changeTextfieldTextDecoration = changeAndPersistLocalStyleActionFactory(
  setTextfieldTextDecoration,
);
export const changeTextfieldTextTransform = changeAndPersistLocalStyleActionFactory(
  setTextfieldTextTransform,
);

export const changePageBackgroundColor = changeAndPersistLocalStyleActionFactory(
  setPageBackgroundColor,
);

export const changeBodyBackgroundColor = changeAndPersistLocalStyleActionFactory(
  setBodyBackgroundColor,
);

export const changeBodyBackgroundImage = changeAndPersistLocalStyleActionFactory(
  setBodyBackgroundImage,
);

export const changeBodyBackgroundPosition = changeAndPersistLocalStyleActionFactory(
  setBodyBackgroundPosition,
);

export const changeBlockMargin = changeAndPersistLocalStyleActionFactory(
  setBlockMargin,
);

export const clearBodyBackgroundImage = () => dispatch =>
  dispatch(changeBodyBackgroundImage(null));

export const insertBodyBackgroundImageFromUploaderList = () => (
  dispatch,
  getState,
) => {
  const state = getState();
  const imageSelected = getLibrarySelectedImage(state);
  dispatch(changeBodyBackgroundImage(imageSelected));
};

export const openInsertBodyBackgroundImageModal = () =>
  openModalDialog({
    type: modalTypes.insertBodyBackgroundImage,
  });

export const openBodyBackgroundImagePreviewModal = (
  imageId,
  dialogId,
) => dispatch => {
  dispatch(previewImage(imageId));
  dispatch(
    openModalDialog({
      type: modalTypes.bodyBackgroundPreviewImage,
      imageId,
      insertImageDialogId: dialogId,
    }),
  );
};
